// The boss duel: the whole team attacks, the boss hits the front creature,
// three minutes at most. One copy for the server's real fight, the Bosses
// page's practice runs and the sim bots' "can we win this?" check.
//
// Every fighter casts its kit by the auto-cast rule on the base chart (battle design 2.1, 3.2), every move starts
// with 35% of its cooldown left, and the level edge counts both ways (3.1): a team above the boss's level hits
// harder and is hit softer. The first beats are spread by team slot. The rng draws the crits (6% at ×1.5) and the damage
// rolls only (battle-consistency.md), so a team well above or below the line wins or loses nearly every time.
import { computeStats, creatureStats } from './stats.ts';
import { beatMs, moveCooldownMs, kitMoves, pickMove, strike, type Fighter } from './combat.ts';
import { relicMult } from './items.ts';
import { keeperMult } from './halls.ts';
import { Rng } from './rng.ts';
import { speciesTrait } from './traits.ts';
import { practiceSeed } from './plan.ts';
import type { BossResult, CreatureDTO } from './protocol.ts';

/** Boss practice runs draw their own set of practice seeds. */
const BOSS_PRACTICE_SALT = 7;

export const BOSS_ROLLS = { ivs: [31, 31, 31, 31, 31, 31] as [number, number, number, number, number, number], quality: 1.1, bloodline: 0, aura: false, trait: 'sturdy' };
/**
 * Boss HP as a multiple of its stats' HP. Re-derived for the move engine and the level edge (battle design phase 2,
 * sim/TUNING_LOG.md 2026-10-13) to keep the boss rule (tests/yomi.test.ts, deterministic since 2026-10-15): the
 * region's five rarest finals with average rolls and four stars always win at the boss's level, an average team with
 * no counter element 5% under it with two stars always loses, and a counter team wins lower than a generic one
 * (Fenrir 14 -> 14.5 on 2026-10-15). The level edge makes a
 * team above the boss's level much stronger than before: the same five with no stars win from about 1.15× its level.
 */
export const BOSS_HP_MULT: Record<string, number> = { fenrir: 14.5, typhon: 14, hachimata: 15 };
export const bossHpMult = (id: string) => BOSS_HP_MULT[id] ?? 14;
const MAX_MS = 180_000;

export function bossFighter(id: string, level: number): Fighter {
  return { speciesId: id, level, stats: computeStats(id, level, BOSS_ROLLS), trait: 'sturdy' };
}

/** halls: the elements whose hall the trainer keeps (TrainerDTO.halls); the keeper's bonus counts against bosses. */
export function creatureFighter(c: CreatureDTO, halls?: readonly string[]): Fighter {
  const stats = creatureStats(c);
  let relicDmg: Fighter['relicDmg'];
  if (c.relic?.stat === 'element') relicDmg = { element: c.relic.element, mult: relicMult(c.relic) };
  else if (c.relic?.stat === 'damage') relicDmg = { mult: relicMult(c.relic) };
  const hallMult = keeperMult(c.species, halls);
  return { speciesId: c.species, level: c.level, stats, trait: speciesTrait(c.rolls.trait, c.species), relicDmg, ...(hallMult !== 1 ? { hallMult } : {}), ...(c.tablet ? { tablet: c.tablet } : {}) };
}

/** One fight. `log` (optional) gets every blow, as the server sends it to the client. */
export function duel(team: { id: number; f: Fighter }[], bf: Fighter, rng: Rng, log?: BossResult['log'], hpMult = bossHpMult(bf.speciesId)): { win: boolean; bossHp: number; bossMaxHp: number } {
  const bossMaxHp = Math.round(bf.stats.hp * hpMult);
  let bossHp = bossMaxHp, time = 0, bossNext = beatMs(bf);
  const hp = team.map((x) => x.f.stats.hp);
  // First beats spread evenly over 0.3-1.0 of a beat by slot (the range the old seeded draw covered).
  const next = team.map((x, i) => beatMs(x.f) * (0.3 + (0.7 * (i + 0.5)) / team.length));
  // When each move is ready again: 35% of its cooldown left at the start.
  const opening = (f: Fighter) => new Map(kitMoves(f).map((m) => [m.id, moveCooldownMs(f, m) * 0.35]));
  const cds = team.map((x) => opening(x.f));
  const bossCd = opening(bf);
  const cast = (f: Fighter, def: Fighter, cd: Map<string, number>, defHp: number) => {
    const mv = pickMove(f, def, 'base', (m) => (cd.get(m.id) ?? 0) <= time, defHp);
    if (mv.slot !== 'basic') cd.set(mv.id, time + moveCooldownMs(f, mv));
    return strike(f, def, mv, 'base', rng);
  };
  while (bossHp > 0 && hp.some((h) => h > 0) && time < MAX_MS) {
    time += 100;
    for (let i = 0; i < team.length; i++) {
      if (hp[i] <= 0 || time < next[i]) continue;
      const h = cast(team[i].f, bf, cds[i], bossHp);
      bossHp = Math.max(0, bossHp - h.dmg);
      next[i] = time + beatMs(team[i].f);
      log?.push({ by: 'team', id: team[i].id, dmg: h.dmg, crit: h.crit, hp: bossHp, mv: h.mv, ms: time });
      if (bossHp <= 0) break;
    }
    if (bossHp > 0 && time >= bossNext) {
      const front = hp.findIndex((x) => x > 0);
      if (front >= 0) {
        const h = cast(bf, team[front].f, bossCd, hp[front]);
        hp[front] = Math.max(0, hp[front] - h.dmg);
        log?.push({ by: 'boss', id: team[front].id, dmg: h.dmg, crit: h.crit, hp: hp[front], mv: h.mv, ms: time });
      }
      bossNext = time + beatMs(bf) * 0.8;
    }
  }
  return { win: bossHp <= 0, bossHp, bossMaxHp };
}

/** Practice runs on fixed practice seeds (never a real fight's seed, shared/plan.ts): how often the team wins and the average share of the boss's HP it takes. */
export function practiceRuns(bossId: string, level: number, team: CreatureDTO[], runs = 20, halls?: readonly string[]): { wins: number; dealt: number; runs: number } {
  const bf = bossFighter(bossId, level);
  const fs = team.map((c) => ({ id: c.id, f: creatureFighter(c, halls) }));
  let wins = 0, dealt = 0;
  for (let r = 0; r < runs; r++) {
    const res = duel(fs, bf, new Rng(practiceSeed(r, BOSS_PRACTICE_SALT)));
    if (res.win) wins++;
    dealt += 1 - res.bossHp / res.bossMaxHp;
  }
  return { wins, dealt: dealt / runs, runs };
}
